import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { UseI18n } from '../../../../common/hooks/useI18n'
import { UseTimeZone } from '../../../../common/hooks/useTimeZone'
import { Element } from '../../../../common/html/element'
import { FORMAT, formatDateTime } from '../../../../helpers/datetime'
import { formatVal } from '../../../../helpers/value'
import { COMPANY_TYPES } from '../../../../utils/common'
import { INDICATOR_ID, INDICATOR_TYPE } from '../constant'
import { isNumeric } from '../helper'
import {
  selectData,
  selectLocationById,
  selectScreeningIndicatorById,
} from '../store/slice'
import classes from './index.module.scss'

const getDataSelector = (dataSource) => {
  switch (dataSource) {
    case 'Status':
      return 'companyStatus'
    case 'LegalForms':
      return 'legalForm'
    case 'CompanyTypes':
      return 'companyType'
    case 'ListingStatus':
      return 'listingStatus'
    default:
      return
  }
}

export const Indicator = ({ data, currency }) => {
  const timezone = UseTimeZone()
  const locale = useSelector((state) => state.i18n.locale)

  const indicatorInfo = useSelector(
    selectScreeningIndicatorById(data.screeningIndicatorId),
  )

  const dropdownListData =
    useSelector(selectData(getDataSelector(indicatorInfo.dataSource))) || []
  const locationById = useSelector(selectLocationById())

  const getComTypeText = () => {
    switch (indicatorInfo.comTypeCode) {
      case COMPANY_TYPES.CORPORATE:
        return UseI18n('screening.CORPORATE')
      case COMPANY_TYPES.BANK:
        return UseI18n('screening.BANK')
      case COMPANY_TYPES.SECURITIES:
        return UseI18n('screening.SECURITIES')
      case COMPANY_TYPES.INSURANCE:
        return UseI18n('screening.INSURANCE')
      default:
        return ''
    }
  }

  const renderIndicatorName = () => {
    switch (data.indicatorType) {
      case INDICATOR_TYPE.RatioRange:
      case INDICATOR_TYPE.FinancialRange:
        return `${indicatorInfo.screeningIndicatorName} (${getComTypeText()})`
      default:
        return indicatorInfo.screeningIndicatorName
    }
  }

  const renderIndicatorValue = () => {
    switch (data.indicatorType) {
      case INDICATOR_TYPE.TextBox:
        return Object.values(data.data)
          .filter(Boolean)
          .map((condition, index) => (
            <Element key={index} className={classes.mb4}>
              {condition.value}
            </Element>
          ))
      case INDICATOR_TYPE.NumberRange:
      case INDICATOR_TYPE.FloatRange:
        return Object.values(data.data)
          .filter(Boolean)
          .map((condition, index) => {
            const from = condition.from
            const to = condition.to
            const formatFrom = formatVal(from, locale, false, 0)
            const formatTo = formatVal(to, locale, false, 0)
            return (
              <Element key={index} className={classes.mb4}>
                {isNumeric(from) &&
                  isNumeric(to) &&
                  `${formatFrom} - ${formatTo}`}
                {isNumeric(from) && !isNumeric(to) && <>&ge; {formatFrom}</>}
                {!isNumeric(from) && isNumeric(to) && <>&le; {formatTo}</>}
              </Element>
            )
          })
      case INDICATOR_TYPE.DateTimeRange:
        return Object.values(data.data)
          .filter(Boolean)
          .map((condition, index) => {
            const from = condition.from
              ? formatDateTime(condition.from, FORMAT.DATE, locale, timezone)
              : undefined
            const to = condition.to
              ? formatDateTime(condition.to, FORMAT.DATE, locale, timezone)
              : undefined
            return (
              <Element key={index} className={classes.mb4}>
                {from && to && `${from} - ${to}`}
                {from && !to && <>&ge; {from}</>}
                {!from && to && <>&le; {to}</>}
              </Element>
            )
          })
      case INDICATOR_TYPE.DropdownList:
        return dropdownListData.map((item) => {
          if (data.data[item.id] && !data.data[item.parentId]) {
            return (
              <Element key={item.id} className={classes.mb4}>
                {item.name}
              </Element>
            )
          }
          return null
        })
      case INDICATOR_TYPE.MultipleDropdownList:
        const locationData = []

        data.data?.forEach((province) => {
          locationData.push([province.Province.LocationId])
          const startProvinceIndex = locationData.length - 1
          province.Province.Districts.forEach((district, districtIndex) => {
            if (districtIndex === 0) {
              locationData[startProvinceIndex].push(district.LocationId)
              district.Wards.forEach((ward, wardIndex) => {
                if (wardIndex === 0) {
                  locationData[startProvinceIndex].push(ward.LocationId)
                } else {
                  locationData.push([
                    province.Province.LocationId,
                    district.LocationId,
                    ward.LocationId,
                  ])
                }
              })
            } else {
              locationData.push([
                province.Province.LocationId,
                district.LocationId,
              ])
              const startDistrictIndex = locationData.length - 1
              district.Wards.forEach((ward, wardIndex) => {
                if (wardIndex === 0) {
                  locationData[startDistrictIndex].push(ward.LocationId)
                } else {
                  locationData.push([
                    province.Province.LocationId,
                    district.LocationId,
                    ward.LocationId,
                  ])
                }
              })
            }
          })
        })

        return locationData
          .map((locationIds) => locationIds.map((id) => locationById[id].name))
          .map((locationNames, index) => (
            <Element key={index} className={classes.mb4}>
              {locationNames.join(' - ')}
            </Element>
          ))
      case INDICATOR_TYPE.RatioRange:
      case INDICATOR_TYPE.FinancialRange:
        const isFinancialRange =
          data.indicatorType === INDICATOR_TYPE.FinancialRange
        return Object.values(data.data)
          .filter(Boolean)
          .map((condition, index) => {
            const from = condition.from
            const to = condition.to
            const formatFrom = formatVal(
              from,
              locale,
              false,
              isFinancialRange ? 0 : 2,
            )
            const formatTo = formatVal(
              to,
              locale,
              false,
              isFinancialRange ? 0 : 2,
            )
            if (isFinancialRange) {
              return (
                <Element key={index} className={`dFlex gap2 ${classes.mb4}`}>
                  <Element className="fw600">{condition.year}</Element>
                  <Element>
                    {isNumeric(from) &&
                      isNumeric(to) &&
                      `${currency} ${formatFrom} - ${currency} ${formatTo}`}
                    {isNumeric(from) && !isNumeric(to) && (
                      <>
                        &ge; {currency} {formatFrom}
                      </>
                    )}
                    {!isNumeric(from) && isNumeric(to) && (
                      <>
                        &le; {currency} {formatTo}
                      </>
                    )}
                  </Element>
                </Element>
              )
            } else {
              return (
                <Element key={index} className={`dFlex gap2 ${classes.mb4}`}>
                  <Element className="fw600">{condition.year}</Element>
                  <Element>
                    {isNumeric(from) &&
                      isNumeric(to) &&
                      `${formatFrom} - ${formatTo}`}
                    {isNumeric(from) && !isNumeric(to) && (
                      <>&ge; {formatFrom}</>
                    )}
                    {!isNumeric(from) && isNumeric(to) && <>&le; {formatTo}</>}
                  </Element>
                </Element>
              )
            }
          })
      case INDICATOR_TYPE.Radio:
        if (
          data.screeningIndicatorId === INDICATOR_ID.HAS_INDEPENDENT_COMPANY
        ) {
          return (
            <Translate
              value={
                data.data[0]
                  ? 'screening.INDEPENDENT_COMPANIES'
                  : 'screening.NON_INDEPENDENT_COMPANIES'
              }
            />
          )
        }
        return (
          <Translate
            value={
              data.data[0]
                ? 'screening.HAS_PARENT_COMPANY'
                : 'screening.HAS_NO_PARENT_COMPANY'
            }
          />
        )
      default:
        return null
    }
  }

  return (
    <Element className={classes.conditionItem} style={{ color: '#3A414B' }}>
      <Element className={classes.conditionName}>
        {renderIndicatorName()}
      </Element>
      <Element className={classes.conditionValue}>
        {renderIndicatorValue()}
      </Element>
    </Element>
  )
}
